import { DownloadIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { analytics, extensionOf } from "../../lib/telemetry/analytics.ts";

/**
 * Un archivo que el agente generó, con su enlace.
 *
 * El enlace es una **llave al portador**: quien lo tenga descarga, sin token.
 * Por eso caduca, y por eso aquí se dice cuándo — alguien que vaya a
 * reenviarlo por correo merece saber que deja de servir.
 */
interface Props {
  props: Record<string, unknown>;
}

const text = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null;

/**
 * El tipo del archivo, para la telemetría, cuando la herramienta no lo dice:
 * el agente solo genera estos.
 */
const MIME_BY_EXT: Readonly<Record<string, string>> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  csv: "text/csv",
  pdf: "application/pdf",
};

export function Download({ props }: Props) {
  const url = text(props["url"]);
  const fileName = text(props["fileName"]) ?? "archivo";
  const rows = typeof props["rowCount"] === "number" ? props["rowCount"] : null;
  const size =
    typeof props["sizeBytes"] === "number" ? props["sizeBytes"] : null;
  const expiresAt = text(props["expiresAt"]);
  const sheets = sheetsOf(props["sheets"]);

  if (url === null) {
    return (
      <Alert variant="destructive">
        <AlertTitle>No hay enlace de descarga</AlertTitle>
        <AlertDescription>
          La herramienta generó el archivo pero no devolvió dónde está.
        </AlertDescription>
      </Alert>
    );
  }

  const meta = [
    rows === null
      ? null
      : `${rows.toLocaleString("es")} ${rows === 1 ? "fila" : "filas"}`,
    size === null ? null : `${String(Math.max(1, Math.round(size / 1024)))} KB`,
    expiresAt === null ? null : `caduca ${expiry(expiresAt)}`,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return (
    <a
      href={url}
      download={fileName}
      onClick={() => {
        const mimeType =
          text(props["mimeType"]) ?? MIME_BY_EXT[extensionOf(fileName)] ?? null;
        analytics.chat.fileDownloaded({
          ext: extensionOf(fileName),
          fileName,
          ...(mimeType === null ? {} : { mimeType }),
          ...(rows === null ? {} : { rowCount: rows }),
        });
      }}
      className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 transition-colors hover:border-primary"
    >
      <DownloadIcon className="size-4 shrink-0 text-primary" />
      <span className="grid min-w-0">
        <span className="truncate font-medium">{fileName}</span>
        <span className="text-muted-foreground text-sm">{meta}</span>
        {sheets.length > 1 && (
          // Un libro de varias hojas dice cuáles lleva: es lo que se pidió
          // ("un reporte con dos hojas") y lo que hay que comprobar antes de
          // reenviarlo.
          <span className="text-muted-foreground truncate text-xs">
            {sheets.length} hojas:{" "}
            {sheets
              .map(
                (sheet) =>
                  `${sheet.name} (${sheet.rowCount.toLocaleString("es")})`,
              )
              .join(" · ")}
          </span>
        )}
      </span>
    </a>
  );
}

/** En días, que es como piensa quien se pregunta si el enlace le va a servir. */
const expiry = (iso: string): string => {
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return "hoy";
  if (days === 1) return "mañana";
  return `en ${String(days)} días`;
};

interface Sheet {
  readonly name: string;
  readonly rowCount: number;
}

/**
 * Las hojas de un libro (`export_workbook`, 1.4). Un archivo de una sola
 * tabla (`export_dataset`) no las trae, y entonces no hay nada que decir.
 */
const sheetsOf = (value: unknown): readonly Sheet[] =>
  Array.isArray(value)
    ? value.filter(
        (sheet): sheet is Sheet =>
          typeof sheet === "object" &&
          sheet !== null &&
          typeof (sheet as Sheet).name === "string" &&
          typeof (sheet as Sheet).rowCount === "number",
      )
    : [];
